import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Switch,
  FormControlLabel,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

import UploadBadge from '../../components/UploadBadge';
import UploadAudio from '../../components/UploadAudio';
import { createAudio, updateAudio } from '../../http/audio';
import { uploadImage, getUploadedMediaRecord } from '../../http/uploads';
import Spinner from '../../components/Spinner';
import { toast } from 'react-toastify';
import { handleToast } from '../../utils/common';
import CustomMultiSelect from '../../components/FormElements/MultiSelect';
import CustomTextField from '../../components/FormElements/CustomTextField';
import defaultAudioThumbnail from '../../assets/lightmodeicon.png';

const isValidMediaId = (id) =>
  typeof id === 'string' && /^[0-9a-fA-F]{24}$/.test(id);

const getCategoryIdsFromChallenges = (challengeIds, challengesList) => {
  const ids = new Set();
  (challengeIds || []).forEach((challengeId) => {
    const challenge = (challengesList || []).find((c) => c._id === challengeId);
    const categoryId = challenge?.categoryId || challenge?.category?._id;
    if (categoryId) {
      ids.add(categoryId);
    }
  });
  return [...ids];
};

/**
 * Audio create/update expect `image` as an already-uploaded media ObjectId string.
 * Multipart file fields other than `file` are rejected by multer.single('file').
 */
const resolveAudioImageMediaId = async (imageToUse) => {
  if (!imageToUse) return null;

  if (isValidMediaId(imageToUse)) {
    return imageToUse;
  }

  if (imageToUse instanceof File || imageToUse instanceof Blob) {
    const uploadFormData = new FormData();
    uploadFormData.append(
      'file',
      imageToUse instanceof File
        ? imageToUse
        : new File([imageToUse], 'audio-thumbnail.png', {
            type: imageToUse.type || 'image/png',
          })
    );
    uploadFormData.append('folder', 'badge');
    const uploadRes = await uploadImage(uploadFormData);
    const media = getUploadedMediaRecord(uploadRes);
    return media?._id || null;
  }

  // Existing remote URL (edit mode) — leave image unchanged on the server.
  if (typeof imageToUse === 'string' && imageToUse.startsWith('http')) {
    return null;
  }

  return null;
};

const appendCategoriesToFormData = (formData, categoryIds, statusMap) => {
  categoryIds.forEach((item) => {
    formData.append('categoryIds[]', item);
    formData.append(
      `categoryStatus[${item}]`,
      statusMap[item] ? 'ACTIVE' : 'INACTIVE'
    );
  });
};

const AddAudioModal = ({
  openDialog,
  handleCloseDialog,
  selectedAudio,
  classes,
  view,
  edit,
  categoryTypes,
  closeModalHandler,
  fetchAudios,
  contests,
  challenges,
}) => {
  const [image, setImage] = useState(selectedAudio?.image || null);
  const [title, setTitle] = useState(selectedAudio?.name || '');
  const [audio, setAudio] = useState(selectedAudio?.uri || null);
  const [audioDuration, setAudioDuration] = useState(0);
  const [loading, setLoading] = useState(false);
  const [audioMode, setAudioMode] = useState('regular'); // 'regular' | 'contest' | 'challenge'
  const [selectedCategories, setSelectedCategories] = useState(
    selectedAudio?.categories?.map((item) => item?._id) || []
  );
  const [categoryStatus, setCategoryStatus] = useState({});
  const [selectedContests, setSelectedContests] = useState(
    selectedAudio?.contests?.map((item) => item?._id) || []
  );
  const [selectedChallenges, setSelectedChallenges] = useState(
    selectedAudio?.challenges?.map((item) => item?._id) || []
  );

  const [toastId, setToastId] = useState(null);
  const audioRefStatic = useRef(new Audio(selectedAudio?.uri));

  // Initialize from selectedAudio (for edit/view mode)
  useEffect(() => {
    if (selectedAudio) {
      const cats = selectedAudio?.categories?.map((item) => item?._id);
      setSelectedCategories(cats);
      // Initialize category status from selectedAudio
      const initialStatus = {};
      selectedAudio?.categories?.forEach((cat) => {
        initialStatus[cat._id] = cat.status === 'ACTIVE';
      });
      setCategoryStatus(initialStatus);

      // Set selected contests if they exist
      if (selectedAudio?.contests) {
        setSelectedContests(
          selectedAudio.contests.map((comp) => comp._id)
        );
      }

      // Set selected challenges if they exist
      if (selectedAudio?.challenges) {
        setSelectedChallenges(
          selectedAudio.challenges.map((ch) => ch._id)
        );
      }

      // Pick a sensible mode for edit/view
      const hasContests = (selectedAudio?.contests || []).length > 0;
      const hasChallenges = (selectedAudio?.challenges || []).length > 0;
      if (hasContests) setAudioMode('contest');
      else if (hasChallenges) setAudioMode('challenge');
      else setAudioMode('regular');
    }
  }, [selectedAudio]);

  // Restore last selection when modal opens for add mode
  useEffect(() => {
    if (!selectedAudio && !edit && openDialog && categoryTypes.length > 0) {
      // Initialize with empty state first
      setCategoryStatus({});
      
      // Restore last selection from sessionStorage
      const lastMode = sessionStorage.getItem('lastAudioMode');
      if (lastMode === 'contest') {
        setAudioMode('contest');
        // Auto-select contest mode based on last selection
        const contestCategory = categoryTypes.find(
          (cat) => cat.name === 'Contest' || cat.categoryShortName === 'contest'
        );
        if (contestCategory) {
          setSelectedCategories([contestCategory._id]);
          setCategoryStatus({ [contestCategory._id]: true });
          setSelectedContests([]);
          setSelectedChallenges([]);
        }
      } else if (lastMode === 'challenge') {
        setAudioMode('challenge');
        setSelectedContests([]);
        setSelectedChallenges([]);
      }
    }
  }, [openDialog, edit, categoryTypes, selectedAudio]);

  const handleCloseDialogHandler = () => {
    if (audioRefStatic.current) {
      audioRefStatic.current.pause();
      audioRefStatic.current.currentTime = 0;
    }
    handleCloseDialog();
  };

  const handleImageUpload = (file) => {
    setImage(file);
  };

  const handleCategoryChange = (value) => {
    const newCategories = typeof value === 'string' ? value.split(',') : value;
    setSelectedCategories(newCategories);

    // Update category status for new selections
    const newStatus = { ...categoryStatus };
    newCategories.forEach((catId) => {
      if (!(catId in newStatus)) {
        newStatus[catId] = true; // Default to active for new selections
      }
    });
    // Remove status for unselected categories
    Object.keys(newStatus).forEach((catId) => {
      if (!newCategories.includes(catId)) {
        delete newStatus[catId];
      }
    });
    setCategoryStatus(newStatus);

    // Clear selected contests if Contest category is removed
    const contestCategory = categoryTypes.find(
      (cat) => cat.name === 'Contest'
    );
    if (
      contestCategory &&
      !newCategories.includes(contestCategory._id)
    ) {
      setSelectedContests([]);
    }
  };

  const handleStatusChange = (categoryId) => (event) => {
    const isActive = event.target.checked;
    setCategoryStatus((prev) => ({
      ...prev,
      [categoryId]: isActive,
    }));

    // If this is a contest category and it's being toggled to inactive
    const category = categoryTypes.find((cat) => cat._id === categoryId);
    if (category?.name === 'Contest' && !isActive) {
      setSelectedContests([]);
    }
  };

  const handleContestChange = (value) => {
    const newContests =
      typeof value === 'string' ? value.split(',') : value;
    setSelectedContests(newContests);
  };

  const handleChallengeChange = (value) => {
    const newChallenges =
      typeof value === 'string' ? value.split(',') : value;
    setSelectedChallenges(newChallenges);

    const categoryIds = getCategoryIdsFromChallenges(newChallenges, challenges);
    setSelectedCategories(categoryIds);
    const newStatus = {};
    categoryIds.forEach((catId) => {
      newStatus[catId] = categoryStatus[catId] ?? true;
    });
    setCategoryStatus(newStatus);
  };

  // Quick action: Set up for contest audio
  const handleQuickContestAudio = () => {
    const contestCategory = categoryTypes.find(
      (cat) => cat.name === 'Contest' || cat.categoryShortName === 'contest'
    );
    if (contestCategory) {
      setAudioMode('contest');
      setSelectedCategories([contestCategory._id]);
      setCategoryStatus({ [contestCategory._id]: true });
      // Save to sessionStorage
      sessionStorage.setItem('lastAudioMode', 'contest');
    }
  };

  // Quick action: Set up for challenge audio
  const handleQuickChallengeAudio = () => {
    setAudioMode('challenge');
    sessionStorage.setItem('lastAudioMode', 'challenge');
  };

  // Quick action: Set up for regular audio (non-contest)
  const handleQuickRegularAudio = () => {
    const contestCategory = categoryTypes.find(
      (cat) => cat.name === 'Contest' || cat.categoryShortName === 'contest'
    );
    setAudioMode('regular');
    // Remove contest category if it's selected
    const newCategories = selectedCategories.filter(
      (catId) => catId !== contestCategory?._id
    );
    setSelectedCategories(newCategories);
    // Update status to remove contest category
    const newStatus = { ...categoryStatus };
    if (contestCategory && newStatus[contestCategory._id]) {
      delete newStatus[contestCategory._id];
    }
    setCategoryStatus(newStatus);
    // Save to sessionStorage
    sessionStorage.setItem('lastAudioMode', 'regular');
  };

  const isContestMode = audioMode === 'contest';
  const isChallengeMode = audioMode === 'challenge';

  // Sort contests alphabetically by title
  const sortedContests = React.useMemo(() => {
    return [...contests].sort((a, b) => {
      const titleA = (a.title || '').toLowerCase();
      const titleB = (b.title || '').toLowerCase();
      return titleA.localeCompare(titleB);
    });
  }, [contests]);

  const sortedChallenges = React.useMemo(() => {
    const enriched = (challenges || []).map((ch) => {
      const categoryName =
        ch?.category?.name ||
        ch?.categoryName ||
        ch?.categoryTitle ||
        (typeof ch?.category === 'string' ? ch.category : '');

      const title = ch?.title || '';
      const labelTitle =
        categoryName && title ? `${title} (${categoryName})` : title;

      return {
        ...ch,
        title: labelTitle,
        _sortTitle: title,
      };
    });

    return enriched.sort((a, b) => {
      const titleA = (a._sortTitle || '').toLowerCase();
      const titleB = (b._sortTitle || '').toLowerCase();
      return titleA.localeCompare(titleB);
    });
  }, [challenges]);

  // Helper function to create default thumbnail from lightmodeicon.png
  const createDefaultThumbnail = async () => {
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = defaultAudioThumbnail;
      });

      const canvas = document.createElement('canvas');
      canvas.width = img.width || 200;
      canvas.height = img.height || 200;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);

      const blob = await new Promise((resolve) => {
        canvas.toBlob(resolve, 'image/png');
      });

      if (blob) {
        return new File([blob], 'audio-thumbnail.png', { type: 'image/png' });
      }
    } catch (err) {
      console.error('Error creating default thumbnail:', err);
    }
    return null;
  };

  const submitHandler = async () => {
    // Determine which image to use - use default if no image provided
    let imageToUse = image;
    if (!imageToUse) {
      // No image selected, use default thumbnail
      imageToUse = await createDefaultThumbnail();
    }

    const formData = new FormData();

    // Challenge/contest assignments derive their parent category on the server.
    // Do NOT also send those parents as categoryIds — that creates category-only
    // AudioAssignment rows and triggers the 7-clip-per-category limit (500).
    let categoriesToSend = [];
    let contestsToSend = [];
    let challengesToSend = [];

    if (isChallengeMode) {
      challengesToSend = [...selectedChallenges];
      if (challengesToSend.length > 0) {
        const resolved = getCategoryIdsFromChallenges(
          challengesToSend,
          challenges
        );
        if (resolved.length === 0) {
          handleToast(
            'Could not resolve a category for the selected challenge(s).',
            toast,
            toastId,
            setToastId
          );
          return;
        }
      }
    } else if (isContestMode) {
      contestsToSend = [...selectedContests];
    } else {
      categoriesToSend = [...selectedCategories];
    }

    const categoryStatusToSend = { ...categoryStatus };
    categoriesToSend.forEach((catId) => {
      if (!(catId in categoryStatusToSend)) {
        categoryStatusToSend[catId] = true;
      }
    });

    try {
      setLoading(true);

      // API requires `image` as a media ObjectId string (not a file / not imageId).
      const imageMediaId = await resolveAudioImageMediaId(imageToUse);
      if (!edit && !imageMediaId) {
        handleToast(
          'Could not prepare the audio thumbnail. Please try again.',
          toast,
          toastId,
          setToastId
        );
        return;
      }

      const uploadContext = {
        mode: edit ? 'edit' : 'create',
        audioMode,
        title: title?.trim(),
        audioDuration,
        audioFile:
          audio instanceof File || audio instanceof Blob
            ? { name: audio.name, type: audio.type, size: audio.size }
            : typeof audio === 'string'
              ? { existingUri: true }
              : null,
        categoryIds: categoriesToSend,
        contestIds: contestsToSend,
        challengeIds: challengesToSend,
        audioId: edit ? selectedAudio?._id : undefined,
        imageMediaId,
      };

      if (imageMediaId) {
        formData.append('image', imageMediaId);
      }
      formData.append('name', title);
      appendCategoriesToFormData(formData, categoriesToSend, categoryStatusToSend);
      contestsToSend.forEach((compId) => {
        formData.append('contestIds[]', compId);
      });
      challengesToSend.forEach((challengeId) => {
        formData.append('challengeIds[]', challengeId);
      });

      if (edit) {
        typeof audio !== 'string' && formData.append('file', audio);
        formData.append('id', selectedAudio?._id);
        await updateAudio(formData, uploadContext);
      } else {
        formData.append('file', audio);
        await createAudio(formData, uploadContext);
      }
      
      // Save the current mode to sessionStorage on successful submit (only for add mode)
      if (!edit) {
        const currentMode = isContestMode
          ? 'contest'
          : isChallengeMode
            ? 'challenge'
            : 'regular';
        sessionStorage.setItem('lastAudioMode', currentMode);
      }
      
      await fetchAudios();
      closeModalHandler();
    } catch (err) {
      console.error('[AddAudioModal] Upload failed — see [Audio upload] group above for details');
    } finally {
      setLoading(false);
    }
  };

  const getDisabled = () => {
    // Check if selected categories match the selected audio categories
    const categoriesMatch =
      selectedCategories?.length === selectedAudio?.categories?.length &&
      selectedCategories?.every(
        (value, index) => value === selectedAudio.categories[index]?._id
      );

    // Check if statuses match
    const statusesMatch = selectedCategories?.every((catId) => {
      const currentCategory = selectedAudio?.categories?.find(
        (cat) => cat._id === catId
      );
      const currentStatus = currentCategory?.status === 'ACTIVE';
      const newStatus = categoryStatus[catId];
      return currentStatus === newStatus;
    });

    // Check if contests match
    const contestsMatch =
      selectedContests?.length === selectedAudio?.contests?.length &&
      selectedContests?.every(
        (value, index) => value === selectedAudio.contests[index]?._id
      );

    // Check if challenges match
    const challengesMatch =
      selectedChallenges?.length === selectedAudio?.challenges?.length &&
      selectedChallenges?.every(
        (value, index) => value === selectedAudio.challenges[index]?._id
      );

    // Audio can be created or edited with no category, contest, or
    // challenge assignments, so only media and title are required.
    const allAvailable = image && audio && title?.length > 2;

    const changes =
      image !== selectedAudio?.image ||
      title !== selectedAudio?.name ||
      audio !== selectedAudio?.uri ||
      !categoriesMatch ||
      !statusesMatch ||
      !contestsMatch ||
      !challengesMatch;

    if (edit && allAvailable && changes) return false;
    else if (!edit && allAvailable) return false;
    else return true;
  };

  return (
    <Dialog
      sx={{
        zIndex: 9,
        '& .MuiDialog-container .MuiPaper-elevation': {
          borderRadius: '8px',
          width: '520px',
          maxWidth: '95vw',
        },
      }}
      open={openDialog}
      onClose={handleCloseDialogHandler}
    >
      {loading && <Spinner />}
      <DialogTitle
        sx={{
          padding: '30px 0 15px',
          textAlign: 'center',
          fontWeight: 700,
        }}
      >
        {edit ? 'Edit Audio' : 'Add Audio'}
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Box mt={1}>
          <CustomTextField
            label="Title of the audio"
            onChange={(e) => setTitle(e.target.value)}
            value={title}
            inputProps={{
              readOnly: view,
              maxLength: 30,
              style: { color: 'black' },
            }}
          />
        </Box>
        
        {/* Quick Action Buttons */}
        {!view && (
          <Box sx={{ mt: 2, mb: 2, display: 'flex', gap: 1 }}>
            <Button
              variant={isContestMode ? 'contained' : 'outlined'}
              onClick={handleQuickContestAudio}
              sx={{
                flex: 1,
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '13px',
                py: 1,
                background: isContestMode 
                  ? 'linear-gradient(135deg, #20c997 0%, #17a87d 100%)' 
                  : 'transparent',
                color: isContestMode ? 'white' : '#20c997',
                border: isContestMode ? 'none' : '2px solid #20c997',
                '&:hover': {
                  background: isContestMode 
                    ? 'linear-gradient(135deg, #1db584 0%, #14966d 100%)' 
                    : 'rgba(32, 201, 151, 0.1)',
                  border: isContestMode ? 'none' : '2px solid #1db584',
                },
              }}
            >
              🏆 Contest
            </Button>
            <Button
              variant={isChallengeMode ? 'contained' : 'outlined'}
              onClick={handleQuickChallengeAudio}
              sx={{
                flex: 1,
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '13px',
                py: 1,
                background: isChallengeMode
                  ? 'linear-gradient(135deg, #ff6b6b 0%, #e04848 100%)'
                  : 'transparent',
                color: isChallengeMode ? 'white' : '#ff6b6b',
                border: isChallengeMode ? 'none' : '2px solid #ff6b6b',
                '&:hover': {
                  background: isChallengeMode
                    ? 'linear-gradient(135deg, #f15b5b 0%, #d84141 100%)'
                    : 'rgba(255, 107, 107, 0.1)',
                  border: isChallengeMode ? 'none' : '2px solid #f15b5b',
                },
              }}
            >
              🎯 Challenge
            </Button>
            <Button
              variant={!isContestMode && !isChallengeMode ? 'contained' : 'outlined'}
              onClick={handleQuickRegularAudio}
              sx={{
                flex: 1,
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '13px',
                py: 1,
                background: !isContestMode && !isChallengeMode
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                  : 'transparent',
                color: !isContestMode && !isChallengeMode ? 'white' : '#667eea',
                border: !isContestMode && !isChallengeMode ? 'none' : '2px solid #667eea',
                '&:hover': {
                  background: !isContestMode && !isChallengeMode
                    ? 'linear-gradient(135deg, #5a6fd6 0%, #6a4292 100%)' 
                    : 'rgba(102, 126, 234, 0.1)',
                  border: !isContestMode && !isChallengeMode ? 'none' : '2px solid #5a6fd6',
                },
              }}
            >
              🎵 Regular Audio
            </Button>
          </Box>
        )}

        {/* Hide category selector when contest/challenge mode is active */}
        {!isContestMode && !isChallengeMode && (
          <>
            <CustomMultiSelect
              selectOptions={categoryTypes}
              onChange={handleCategoryChange}
              placeholder={'Select Categories'}
              edit={true}
              categoryTypes={categoryTypes}
              value={selectedAudio?.categories?.map((item) => item?._id)}
            />
            {selectedCategories.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Category Status:
                </Typography>
                {selectedCategories.map((catId) => {
                  const category = categoryTypes.find((cat) => cat._id === catId);
                  if (!category) return null;
                  return (
                    <FormControlLabel
                      key={catId}
                      control={
                        <Switch
                          checked={categoryStatus[catId] ?? false}
                          onChange={handleStatusChange(catId)}
                          disabled={view}
                        />
                      }
                      label={`${category?.name || 'Unknown Category'} - ${
                        categoryStatus[catId] ? 'Active' : 'Inactive'
                      }`}
                    />
                  );
                })}
              </Box>
            )}
          </>
        )}
        {/* Add Contest Selection */}
        {isContestMode && (
          <Box sx={{ mt: 2 }}>
            <CustomMultiSelect
              selectOptions={sortedContests}
              onChange={handleContestChange}
              placeholder={'Select Contests'}
              edit={true}
              value={selectedContests}
            />
          </Box>
        )}

        {/* Challenge selection */}
        {isChallengeMode && (
          <Box sx={{ mt: 2 }}>
            <CustomMultiSelect
              selectOptions={sortedChallenges}
              onChange={handleChallengeChange}
              placeholder={'Select Challenges'}
              edit={true}
              value={selectedChallenges}
            />
          </Box>
        )}
        <Box sx={{ marginTop: '10px' }}>
          <Typography
            sx={{ fontWeight: 600, fontSize: '14px', marginBottom: '10px' }}
          >
            {' '}
            Upload Image{' '}
            <span
              style={{ fontSize: '10px', color: 'gray', fontWeight: 400 }}
            >{`(File size max limit 5mb)`}</span>{' '}
          </Typography>
          <UploadBadge
            onImageUpload={handleImageUpload}
            edit={edit}
            audio={true}
            selectedBadge={selectedAudio}
          />
        </Box>
        <Box sx={{ marginTop: '10px' }}>
          <Typography
            sx={{ fontWeight: 600, fontSize: '14px', marginBottom: '3px' }}
          >
            {' '}
            Audio{' '}
            <span
              style={{ fontSize: '10px', color: 'gray', fontWeight: 400 }}
            >{`(File size max limit 5mb · clips longer than 30s are trimmed)`}</span>{' '}
          </Typography>

          <UploadAudio
            setAudio={setAudio}
            setAudioDuration={setAudioDuration}
            selectedAudio={selectedAudio}
            audioRefStatic={audioRefStatic}
          />
        </Box>
        <Box className={classes.actionsContainer}>
          <Button
            sx={{
              border: '1px solid #888',
              color: '#888',
              borderRadius: '6px',
              fontSize: '14px',
              marginRight: '15px',
              '&:hover': {
                backgroundColor: '#b9b9b9',
                color: '#222',
                borderColor: '#b9b9b9',
              },
            }}
            onClick={handleCloseDialogHandler}
          >
            {view ? 'Close' : 'Cancel'}
          </Button>
          {!view && (
            <Button
              className={classes.button}
              disabled={getDisabled()}
              onClick={submitHandler}
              type="submit"
              sx={{ opacity: getDisabled() ? 0.5 : 1 }}
            >
              {edit ? 'Update Audio' : 'Add Audio'}
            </Button>
          )}
        </Box>
      </DialogContent>
      {/* <ToastContainer /> */}
    </Dialog>
  );
};
export default AddAudioModal;
